import { useState, useEffect, useCallback } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import {
  CaretDown,
  CaretRight,
  ArrowClockwise,
  Bank,
} from "@phosphor-icons/react";
import {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableRow,
  TableHead,
  TableCell,
} from "components/ui/table";
import { Button } from "components/ui/button";
import { Skeleton } from "components/ui/skeleton";
import { formatCurrency } from "shared/utils/currency";

export interface LedgerAccountRow {
  code: string;
  name: string;
  amount: number;
  count: number;
}

interface Props {
  /** Current building scope (optional). */
  buildingId?: string;
  /** Current year filter (optional). */
  year?: number;
}

/**
 * Per renovation grootboekrekening breakdown of what is booked in the Odoo
 * boekhouding, for the active filter (building and/or year — none = all). Shows
 * code · name · amount (excl. btw) with a total row. Read live from Odoo, so it
 * is collapsible and fetches lazily (only once expanded) to avoid a heavy query
 * on every page load; it re-fetches when the filter changes while open.
 */
export const LedgerAccountBreakdown = ({ buildingId, year }: Props) => {
  const { session } = useSession();

  const [isCollapsed, setIsCollapsed] = useState(true);
  const [rows, setRows] = useState<LedgerAccountRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const fetchBreakdown = useCallback(async () => {
    if (!session) return;

    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (buildingId) params.set("buildingId", buildingId);
      if (year) params.set("year", String(year));
      const qs = params.toString();
      const url = qs
        ? `${API_URL}/renovation/ledger-breakdown?${qs}`
        : `${API_URL}/renovation/ledger-breakdown`;

      const response = await fetchWithAuth(url, { method: "GET" }, session);
      if (!response.ok) {
        const errorText = await response
          .text()
          .catch(() => response.statusText);
        throw new Error(
          `Fout bij ophalen van grootboekverdeling (${response.status}): ${errorText}`,
        );
      }
      const data = await response.json();
      setRows(Array.isArray(data) ? data : []);
      setHasLoaded(true);
    } catch (error) {
      // Log so Sentry captures the breadcrumb.
      console.error("[LedgerAccountBreakdown] Error fetching breakdown:", error);
      toast.error("Fout bij ophalen van grootboekverdeling");
    } finally {
      setIsLoading(false);
    }
  }, [session, buildingId, year]);

  // Fetch lazily: only while expanded, and re-fetch when the filter changes.
  useEffect(() => {
    if (!isCollapsed) {
      fetchBreakdown();
    }
  }, [isCollapsed, fetchBreakdown]);

  const total = rows.reduce(
    (acc, r) => ({ amount: acc.amount + r.amount, count: acc.count + r.count }),
    { amount: 0, count: 0 },
  );

  const scopeLabel = [
    buildingId ? `gebouw ${buildingId}` : null,
    year ? `jaar ${year}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="px-6 pt-1 pb-2">
      <div className="rounded-lg border border-neutral-200 bg-white">
        <div className="flex items-center justify-between gap-2 px-4 py-3">
          <button
            type="button"
            onClick={() => setIsCollapsed((c) => !c)}
            className="flex items-center gap-2 text-left"
          >
            {isCollapsed ? (
              <CaretRight size={16} className="text-neutral-secondary-mid" />
            ) : (
              <CaretDown size={16} className="text-neutral-secondary-mid" />
            )}
            <Bank size={18} weight="duotone" className="text-moss-primary" />
            <span className="text-sm font-semibold text-neutral-primary">
              Per grootboekrekening
            </span>
            <span className="text-xs text-neutral-secondary-mid">
              {scopeLabel ? `(${scopeLabel})` : "(alle)"}
            </span>
          </button>
          {!isCollapsed && (
            <Button
              variant="outline"
              size="sm"
              onClick={fetchBreakdown}
              disabled={isLoading}
            >
              <ArrowClockwise
                size={14}
                className={isLoading ? "animate-spin" : ""}
              />
              Vernieuwen
            </Button>
          )}
        </div>

        {!isCollapsed && (
          <div className="border-t border-neutral-200">
            {isLoading && !hasLoaded ? (
              <div className="flex flex-col gap-2 p-4">
                <Skeleton className="h-7 w-full" />
                <Skeleton className="h-7 w-full" />
                <Skeleton className="h-7 w-2/3" />
              </div>
            ) : rows.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-neutral-secondary-mid">
                Geen geboekte renovatiefacturen gevonden voor deze filter.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Grootboekrekening</TableHead>
                    <TableHead>Naam</TableHead>
                    <TableHead className="text-right">Aantal</TableHead>
                    <TableHead className="text-right">
                      Bedrag (excl. btw)
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, index) => {
                    // The backend appends a synthetic "Niet meer in Odoo" row
                    // (no GL code) so the total reconciles with everything
                    // YallyHub tracks. Style it distinctly.
                    const isOrphanRow = !row.code;
                    return (
                      <TableRow
                        key={row.code || `orphan-${index}`}
                        className={isOrphanRow ? "bg-orange-50/60" : undefined}
                      >
                        <TableCell className="font-medium text-neutral-primary tabular-nums">
                          {row.code || "—"}
                        </TableCell>
                        <TableCell
                          className={
                            isOrphanRow
                              ? "font-medium text-orange-700"
                              : "text-neutral-secondary-mid"
                          }
                        >
                          {row.name || "—"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.count}
                        </TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap">
                          {formatCurrency(row.amount)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      className="font-semibold text-neutral-primary"
                    >
                      Totaal
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {total.count}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums whitespace-nowrap">
                      {formatCurrency(total.amount)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LedgerAccountBreakdown;
